import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { SERVER_URL } from "../../constants";
import {
  getStudentsByIds,
  listStaysForStudents,
  updateStay,
  createStay,
  getAdultsByIds,
  listNsrsForAdult,
  createNsr,
  updateNsr,
} from "./api/people";
import { fmtDate } from "./utils/dates";

const d10 = (s) => (s ? String(s).slice(0, 10) : "");
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && bStart <= aEnd;
const normCampus = (v) => String(v ?? "").trim().toLowerCase();

const studentName = (s) =>
  `${s.studentName || s.name || ""} ${s.studentSurname || s.surname || ""}`.trim() || `#${s.id}`;
const adultName = (a) =>
  `${a.adultName || a.name || ""} ${a.adultSurname || a.surname || ""}`.trim() || `#${a.id}`;

// Normalise a student stay and an adult NSR to one shape so the table treats them alike.
const normStudentStay = (st) => ({
  recordId: st.stayId,
  campus: st.campus || "",
  arrivalDate: d10(st.arrivalDate),
  departureDate: d10(st.departureDate),
  raw: st,
});
const normNsr = (n) => ({
  recordId: n.id,
  campus: n.residentialBase || n.residential_base || "",
  arrivalDate: d10(n.arrivaldate || n.arrivalDate),
  departureDate: d10(n.departuredate || n.departureDate),
  raw: n,
});

// Tab 1 of the manual booking-group rework: show every member's stays (students) and NSR
// rows (adults/staff) that overlap the group window, flag the ones that don't match the
// group defaults (campus/dates) in amber, and let the user edit / delete / create / align
// them by hand. Students use stay endpoints; adults use NSR endpoints — same UI.
export default function StaysTab({ token, showToast, studentIds = [], adultIds = [], meta }) {
  const groupCampus = meta?.defaultCampus || "";
  // meta dates are Date objects in state (useBookingMeta), not "YYYY-MM-DD" strings.
  const groupStart = meta?.defaultStartDate ? fmtDate(meta.defaultStartDate) : "";
  const groupEnd = meta?.defaultEndDate ? fmtDate(meta.defaultEndDate) : "";

  const [people, setPeople] = useState([]); // [{ id, kind, name, records: [norm] }]
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editStay, setEditStay] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!studentIds.length && !adultIds.length) {
      setPeople([]);
      return;
    }
    setLoading(true);
    try {
      const [students, studentStays, adults] = await Promise.all([
        studentIds.length ? getStudentsByIds(studentIds, token) : [],
        studentIds.length ? listStaysForStudents(token, studentIds) : {},
        adultIds.length ? getAdultsByIds(adultIds, token) : [],
      ]);

      // Adult NSRs are per-adult (no batch endpoint) — fetch with light concurrency.
      const nsrsByAdult = {};
      for (let i = 0; i < adultIds.length; i += 6) {
        const chunk = adultIds.slice(i, i + 6);
        const res = await Promise.all(
          chunk.map((id) => listNsrsForAdult(token, id).catch(() => []))
        );
        chunk.forEach((id, j) => {
          nsrsByAdult[id] = Array.isArray(res[j]) ? res[j] : [];
        });
      }

      const studentPeople = (Array.isArray(students) ? students : []).map((s) => ({
        id: s.id,
        kind: "student",
        name: studentName(s),
        records: (studentStays[s.id] || []).map(normStudentStay),
      }));
      const adultPeople = (Array.isArray(adults) ? adults : []).map((a) => ({
        id: a.id,
        kind: "adult",
        name: adultName(a),
        records: (nsrsByAdult[a.id] || []).map(normNsr),
      }));
      setPeople([...studentPeople, ...adultPeople]);
    } catch (e) {
      showToast?.("Failed to load stays", "error");
    } finally {
      setLoading(false);
    }
  }, [studentIds, adultIds, token, showToast]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    fetch(`${SERVER_URL}api/campuses/active`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setCampuses(Array.isArray(d) ? d : []))
      .catch(() => setCampuses([]));
  }, [token]);

  const campusNames = useMemo(
    () =>
      Array.from(
        new Set([groupCampus, ...campuses.map((c) => c.name || c.campusName)].filter(Boolean))
      ),
    [campuses, groupCampus]
  );

  const rows = useMemo(
    () =>
      (people || []).map((p) => ({
        person: p,
        records: p.records.filter((r) =>
          overlaps(r.arrivalDate, r.departureDate, groupStart, groupEnd)
        ),
      })),
    [people, groupStart, groupEnd]
  );

  const matchesDefaults = (r) =>
    normCampus(r.campus) === normCampus(groupCampus) &&
    r.arrivalDate === groupStart &&
    r.departureDate === groupEnd;

  const handleSaveStay = async () => {
    if (!editStay || saving) return;
    setSaving(true);
    try {
      const e = editStay;
      if (e.kind === "student") {
        const body = {
          campus: e.campus,
          arrivalDate: e.arrivalDate,
          departureDate: e.departureDate,
        };
        if (e.recordId) await updateStay(token, e.personId, e.recordId, body);
        else await createStay(token, e.personId, body);
      } else {
        const raw = e.raw || {};
        const overnight = raw.overnightResident ?? true;
        const body = {
          residentialBase: e.campus,
          arrivaldate: e.arrivalDate,
          departuredate: e.departureDate,
          overnightResident: overnight,
          gender: overnight ? raw.gender || null : null,
          notes: raw.notes ?? "",
        };
        if (e.recordId) await updateNsr(token, e.recordId, body);
        else await createNsr(token, e.personId, body);
      }
      setEditStay(null);
      await reload();
      showToast?.("Stay saved", "success");
    } catch (e) {
      showToast?.("Failed to save stay", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStay = async (kind, personId, recordId) => {
    if (!window.confirm("Delete this stay?")) return;
    const url =
      kind === "student"
        ? `${SERVER_URL}api/student/${personId}/stay/${recordId}`
        : `${SERVER_URL}api/nsrs/${recordId}`;
    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await reload();
      showToast?.("Stay deleted", "success");
    } catch (e) {
      showToast?.("Failed to delete stay", "error");
    }
  };

  const editExisting = (person, r) =>
    setEditStay({
      kind: person.kind,
      personId: person.id,
      recordId: r.recordId,
      campus: r.campus || groupCampus,
      arrivalDate: r.arrivalDate,
      departureDate: r.departureDate,
      raw: r.raw,
    });
  // "Align" pre-fills the editor with the group defaults so the user confirms rather
  // than blind-overwriting (a member may legitimately differ — e.g. a multi-week stay).
  const alignStay = (person, r) =>
    setEditStay({
      kind: person.kind,
      personId: person.id,
      recordId: r.recordId,
      campus: groupCampus,
      arrivalDate: groupStart,
      departureDate: groupEnd,
      raw: r.raw,
    });
  const createNew = (person) =>
    setEditStay({
      kind: person.kind,
      personId: person.id,
      recordId: null,
      campus: groupCampus,
      arrivalDate: groupStart,
      departureDate: groupEnd,
      raw: null,
    });

  const canSave =
    editStay?.campus && editStay?.arrivalDate && editStay?.departureDate && !saving;

  const nameCell = (person) => (
    <>
      {person.name}
      {person.kind === "adult" ? (
        <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.6 }}>
          · adult
        </Typography>
      ) : null}
    </>
  );
  // Light-yellow name cell for adults (group leaders/parents travelling with the group —
  // not our staff), matching the amber tint used on the staff scheduling tables.
  const nameCellSx = (person) => (person.kind === "adult" ? { bgcolor: "#fff8e1" } : undefined);

  return (
    <Box sx={{ p: 1 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={700}>
          Group defaults
        </Typography>
        <Typography variant="body2">
          {groupCampus || "—"} · {groupStart || "?"} → {groupEnd || "?"}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          Each member&apos;s stays (students) and NSR rows (staff) that overlap the group
          window. Amber = doesn&apos;t match the defaults (campus or dates).
        </Typography>
      </Paper>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2">Loading stays…</Typography>
        </Stack>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Member</TableCell>
              <TableCell>Campus</TableCell>
              <TableCell>Arrival</TableCell>
              <TableCell>Departure</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.flatMap(({ person, records }) =>
              records.length === 0
                ? [
                    <TableRow key={`none-${person.kind}-${person.id}`} sx={{ bgcolor: "#fff4e5" }}>
                      <TableCell sx={nameCellSx(person)}>{nameCell(person)}</TableCell>
                      <TableCell colSpan={3} sx={{ opacity: 0.75 }}>
                        No {person.kind === "adult" ? "NSR" : "stay"} overlapping the group window
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Button size="small" onClick={() => createNew(person)}>
                          Create {person.kind === "adult" ? "NSR" : "stay"}
                        </Button>
                      </TableCell>
                    </TableRow>,
                  ]
                : records.map((r, i) => {
                    const ok = matchesDefaults(r);
                    return (
                      <TableRow
                        key={`rec-${person.kind}-${person.id}-${r.recordId}`}
                        sx={ok ? undefined : { bgcolor: "#fff4e5" }}
                      >
                        <TableCell sx={nameCellSx(person)}>{i === 0 ? nameCell(person) : ""}</TableCell>
                        <TableCell>{r.campus || "—"}</TableCell>
                        <TableCell>{r.arrivalDate}</TableCell>
                        <TableCell>{r.departureDate}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {!ok && (
                            <Button size="small" onClick={() => alignStay(person, r)}>
                              Align
                            </Button>
                          )}
                          <IconButton
                            size="small"
                            aria-label="Edit stay"
                            onClick={() => editExisting(person, r)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete stay"
                            onClick={() => handleDeleteStay(person.kind, person.id, r.recordId)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={Boolean(editStay)} onClose={() => setEditStay(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {editStay?.recordId ? "Edit" : "Create"}{" "}
          {editStay?.kind === "adult" ? "NSR" : "stay"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="stay-campus">Campus</InputLabel>
              <Select
                labelId="stay-campus"
                label="Campus"
                value={editStay?.campus || ""}
                onChange={(e) => setEditStay((s) => ({ ...s, campus: e.target.value }))}
              >
                {campusNames.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Arrival"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editStay?.arrivalDate || ""}
              onChange={(e) => setEditStay((s) => ({ ...s, arrivalDate: e.target.value }))}
            />
            <TextField
              label="Departure"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={editStay?.departureDate || ""}
              onChange={(e) => setEditStay((s) => ({ ...s, departureDate: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditStay(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveStay} disabled={!canSave}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
